<template>
    <div class="multi-search-select position-relative">
        <div class="mss-box form-control h-auto" :class="{'mss-disabled': disabled}">
            <ul v-if="tags.length > 0" class="list-inline d-inline-block mb-1 w-100">
                <li v-for="tag in tags" :key="tag" class="list-inline-item mb-1">
                    <b-form-tag :title="tag"
                                :disabled="disabled"
                                variant="light"
                                class="mss-tag"
                                @remove="removeTag(tag)">{{ tag }}
                    </b-form-tag>
                </li>
            </ul>
            <b-input-group size="sm">
                <b-form-input v-model="keyword"
                              :disabled="disabled"
                              :placeholder="placeholder"
                              autocomplete="off"
                              type="text"
                              class="mss-input"
                              @input="onKeywordInput"
                              @focus="focused = true"
                              @blur="onBlur"
                              @keydown.native="onKeydown"
                              @paste.native="onPaste"/>
                <b-input-group-append>
                    <b-button :disabled="disabled || !keyword"
                              variant="outline-secondary"
                              @mousedown.native.prevent
                              @click="commitKeyword">
                        <i class="fa fa-fw fa-plus"></i>
                    </b-button>
                </b-input-group-append>
            </b-input-group>
        </div>
        <div v-if="dropdownVisible" class="mss-dropdown">
            <div v-if="loading" class="mss-item text-muted">
                <i class="fa fa-fw fa-spinner fa-spin"></i>
            </div>
            <div v-for="(item, index) in suggestions"
                 v-else
                 :key="item"
                 :class="['mss-item', {'mss-item-active': index === activeIndex}]"
                 @mousedown.prevent="addTag(item)"
                 @mouseenter="activeIndex = index">{{ item }}
            </div>
        </div>
    </div>
</template>

<script>
/**
 * 带远程自动补全的多选输入框（em-filter 通用组件）。
 *
 * 用法（filterConfigs / gridFields 的 metadata.form）：
 *   form: {
 *       type: 'multiSearchSelect',
 *       suggestUrl: '/sithMesStoolPrPrice/suggest',   // 只写路径，base url 由 EmFormHelper 拼
 *       suggestField: 'vendor'
 *   }
 *
 * 行为：
 * 1. 输入 >=2 个字符后防抖 300ms 请求建议接口，取回的候选值以下拉列表展示，点选后变成 tag；
 * 2. 回车 / 失焦 会把当前输入直接变成 tag（建议接口没命中也允许手工输入）；
 * 3. 粘贴逗号、分号、换行、制表符分隔的一串值时，一次性拆成多个 tag
 *    （采购员从 Excel 贴一整列物料号是高频动作）。
 *
 * v-model 的值是字符串数组；为了兼容 em-filter 里 "" 的初始值，传入字符串时会自动按分隔符拆开。
 */

// 粘贴内容的分隔符：逗号(半角/全角)、分号(半角/全角)、换行、制表符
const SPLIT_PATTERN = /[,，;；\n\r\t]+/;
const DEBOUNCE_MS = 300;
// 建议接口返回的 top N
const DEFAULT_LIMIT = 20;
// 触发建议接口的最小关键字长度。
// 建议接口是 DISTINCT + ORDER BY + LIMIT，LIMIT 省不掉扫描量，单字符关键字几乎等于扫全表，
// 所以门槛定在 2。后端 SithMesStoolPrPriceGenerationRepository.SUGGEST_MIN_KEYWORD_LENGTH 同值兜底。
const MIN_KEYWORD_LENGTH = 2;

export default {
    name: "MultiSearchSelect",
    props: {
        // 已选值，数组；也兼容传入空字符串（em-filter 的初始值/清空值）
        value: {
            type: [Array, String],
            default: () => []
        },
        // 建议接口地址，为空则不做远程补全，仅支持手工输入/粘贴
        suggestUrl: {
            type: String,
            default: ""
        },
        // 建议接口的字段参数
        suggestField: {
            type: String,
            default: ""
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: ""
        },
        // 建议列表最多展示条数
        limit: {
            type: Number,
            default: DEFAULT_LIMIT
        }
    },
    data() {
        return {
            tags: [],
            keyword: "",
            suggestions: [],
            loading: false,
            focused: false,
            activeIndex: -1,
            timer: null
        };
    },
    computed: {
        dropdownVisible() {
            return this.focused && !!this.suggestUrl && this.keyword.trim().length >= MIN_KEYWORD_LENGTH
                && (this.loading || this.suggestions.length > 0);
        }
    },
    watch: {
        value: {
            handler(newValue) {
                let normalized = this.normalize(newValue);
                // 只在外部真的改了值时才覆盖，避免自己 emit 引起的回环
                if (!this.sameValues(normalized, this.tags)) {
                    this.tags = normalized;
                }
            },
            immediate: true
        }
    },
    beforeDestroy() {
        this.clearTimer();
    },
    methods: {
        sameValues(a, b) {
            return a.length === b.length && a.every((v, i) => v === b[i]);
        },
        normalize(value) {
            if (Array.isArray(value)) {
                return value
                    .filter(v => v !== null && v !== undefined && String(v).trim() !== "")
                    .map(v => String(v).trim());
            }
            if (typeof value === "string" && value.trim() !== "") {
                return this.split(value);
            }
            return [];
        },
        split(text) {
            return String(text).split(SPLIT_PATTERN)
                .map(v => v.trim())
                .filter(v => v !== "");
        },
        clearTimer() {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
        },
        emitValue() {
            this.$emit("input", this.tags.slice());
        },
        addTags(values) {
            let next = this.tags.slice();
            values.forEach(v => {
                if (v !== "" && next.indexOf(v) === -1) {
                    next.push(v);
                }
            });
            this.keyword = "";
            this.suggestions = [];
            this.activeIndex = -1;
            if (next.length !== this.tags.length) {
                this.tags = next;
                this.emitValue();
            }
        },
        addTag(value) {
            this.addTags([value]);
        },
        removeTag(tag) {
            let index = this.tags.indexOf(tag);
            if (index === -1) {
                return;
            }
            let next = this.tags.slice();
            next.splice(index, 1);
            this.tags = next;
            this.emitValue();
        },
        commitKeyword() {
            if (this.keyword.trim() === "") {
                return;
            }
            this.addTags(this.split(this.keyword));
        },
        onKeywordInput() {
            this.activeIndex = -1;
            // 用户手打了分隔符时也直接切成 tag
            if (SPLIT_PATTERN.test(this.keyword)) {
                this.clearTimer();
                this.addTags(this.split(this.keyword));
                return;
            }
            this.clearTimer();
            if (!this.suggestUrl || this.keyword.trim().length < MIN_KEYWORD_LENGTH) {
                this.suggestions = [];
                return;
            }
            this.timer = setTimeout(this.fetchSuggestions, DEBOUNCE_MS);
        },
        onKeydown(event) {
            if (event.key === "Enter") {
                event.preventDefault();
                if (this.activeIndex >= 0 && this.suggestions[this.activeIndex]) {
                    this.addTag(this.suggestions[this.activeIndex]);
                } else {
                    this.commitKeyword();
                }
            } else if (event.key === "ArrowDown" && this.suggestions.length > 0) {
                event.preventDefault();
                this.activeIndex = (this.activeIndex + 1) % this.suggestions.length;
            } else if (event.key === "ArrowUp" && this.suggestions.length > 0) {
                event.preventDefault();
                this.activeIndex = (this.activeIndex - 1 + this.suggestions.length) % this.suggestions.length;
            } else if (event.key === "Backspace" && this.keyword === "" && this.tags.length > 0) {
                this.removeTag(this.tags[this.tags.length - 1]);
            }
        },
        onPaste(event) {
            let clipboard = event.clipboardData || window.clipboardData;
            if (!clipboard) {
                return;
            }
            let text = clipboard.getData("text");
            if (!text) {
                return;
            }
            let values = this.split(text);
            // 只有真的是多值（带分隔符）才拦截，单个值仍然走正常输入 + 补全
            if (values.length > 1) {
                event.preventDefault();
                this.clearTimer();
                this.addTags(values);
            }
        },
        onBlur() {
            this.focused = false;
            // 失焦时把还没提交的输入也变成 tag，避免用户以为已经选上了
            this.commitKeyword();
        },
        fetchSuggestions() {
            let keyword = this.keyword.trim();
            if (!this.suggestUrl || keyword.length < MIN_KEYWORD_LENGTH) {
                return;
            }
            this.loading = true;
            let url = this.suggestUrl
                + (this.suggestUrl.indexOf("?") === -1 ? "?" : "&")
                + "field=" + encodeURIComponent(this.suggestField)
                + "&keyword=" + encodeURIComponent(keyword);
            this.$store.dispatch("api/get", {url})
                .then(response => {
                    // 请求返回时用户可能已经改了输入，丢弃过期结果
                    if (this.keyword.trim() !== keyword) {
                        return;
                    }
                    let list = Array.isArray(response) ? response : [];
                    this.suggestions = list.filter(v => this.tags.indexOf(v) === -1).slice(0, this.limit);
                })
                .catch(() => {
                    this.suggestions = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
};
</script>

<style scoped>
.mss-box {
    padding: .25rem;
}

.mss-disabled {
    background-color: #e9ecef;
}

.mss-tag {
    max-width: 100%;
}

.mss-dropdown {
    position: absolute;
    z-index: 1050;
    left: 0;
    right: 0;
    top: 100%;
    max-height: 220px;
    overflow-y: auto;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, .15);
    border-radius: .25rem;
    box-shadow: 0 .25rem .5rem rgba(0, 0, 0, .15);
}

.mss-item {
    padding: .25rem .5rem;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mss-item-active {
    background-color: #f0f4ff;
}
</style>
